import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { clearModuleCache } from '@/lib/hasModuleAccess';
import Swal from 'sweetalert2';

interface TenantInfo { id: string; name: string; slug: string; plan_code: string; status: string; }
interface SaasModule { id: string; name: string; category: string; is_core: boolean; price_monthly: number; }
interface TenantModuleRow { tenant_id: string; module_id: string; is_enabled: boolean; source: string; price_monthly: number; }

const CATEGORIES = ['Production', 'Website', 'Automation', 'Settings'];

const CAT_STYLE: Record<string, { dot: string; label: string; icon_bg: string; icon_text: string }> = {
  Production: { dot: 'bg-emerald-400', label: 'text-emerald-600', icon_bg: 'bg-emerald-50', icon_text: 'text-emerald-600' },
  Website:    { dot: 'bg-cyan-400',    label: 'text-cyan-600',    icon_bg: 'bg-cyan-50',    icon_text: 'text-cyan-600' },
  Automation: { dot: 'bg-amber-400',   label: 'text-amber-600',   icon_bg: 'bg-amber-50',   icon_text: 'text-amber-600' },
  Settings:   { dot: 'bg-rose-400',    label: 'text-rose-600',    icon_bg: 'bg-rose-50',    icon_text: 'text-rose-600' },
};

const MODULE_ICONS: Record<string, string> = {
  clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
  jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
  basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
  media_packages: 'ri-folder-image-line', dvd_online: 'ri-disc-line', photo_selection: 'ri-image-line',
  final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
  custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
};

const PLAN_STYLES: Record<string, string> = {
  free: 'bg-slate-100 text-slate-500', pro: 'bg-amber-50 text-amber-700', enterprise: 'bg-emerald-50 text-emerald-700',
};

const SOURCE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  plan:   { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Plan' },
  addon:  { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Add-on' },
  manual: { bg: 'bg-sky-50',     text: 'text-sky-700',     label: 'Manual' },
  trial:  { bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'Trial' },
};

export default function TenantModulesPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant]     = useState<TenantInfo | null>(null);
  const [modules, setModules]   = useState<SaasModule[]>([]);
  const [planSet, setPlanSet]   = useState<Set<string>>(new Set());
  const [tmMap, setTmMap]       = useState<Record<string, TenantModuleRow>>({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    const [tenantRes, modulesRes] = await Promise.all([
      supabase.from('saas_tenants').select('id, name, slug, plan_code, status').eq('id', tenantId).maybeSingle(),
      supabase.from('saas_modules').select('id, name, category, is_core, price_monthly').order('category').order('name'),
    ]);
    if (tenantRes.error || !tenantRes.data) { setError('Tenant no encontrado.'); setLoading(false); return; }
    const t = tenantRes.data as TenantInfo;
    setTenant(t);
    const [pmRes, tmRes] = await Promise.all([
      supabase.from('saas_plan_modules').select('module_id, is_included').eq('plan_id', t.plan_code).eq('is_included', true),
      supabase.from('tenant_modules').select('*').eq('tenant_id', tenantId),
    ]);
    const ps = new Set((pmRes.data ?? []).map((r: { module_id: string }) => r.module_id));
    const tm: Record<string, TenantModuleRow> = {};
    ((tmRes.data ?? []) as TenantModuleRow[]).forEach((r) => { tm[r.module_id] = r; });
    setModules((modulesRes.data ?? []) as SaasModule[]);
    setPlanSet(ps);
    setTmMap(tm);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await load();
    };
    init();
  }, [navigate, load]);

  const isEnabled = (moduleId: string): boolean => tmMap[moduleId] ? tmMap[moduleId].is_enabled : planSet.has(moduleId);
  const getSource = (moduleId: string): string => tmMap[moduleId] ? tmMap[moduleId].source : planSet.has(moduleId) ? 'plan' : '';
  const getPrice  = (mod: SaasModule): number => tmMap[mod.id] ? tmMap[mod.id].price_monthly : planSet.has(mod.id) ? 0 : mod.price_monthly;

  const toggle = async (mod: SaasModule) => {
    if (!tenant || toggling) return;
    setToggling(mod.id);
    const current = isEnabled(mod.id);
    const newVal  = !current;
    const src     = planSet.has(mod.id) ? 'plan' : 'addon';
    const price   = planSet.has(mod.id) ? 0 : mod.price_monthly;
    const { error: err } = await supabase.from('tenant_modules').upsert({
      tenant_id: tenant.id, module_id: mod.id, is_enabled: newVal, source: src, price_monthly: price, updated_at: new Date().toISOString(),
    }, { onConflict: 'tenant_id,module_id' });
    if (err) { Swal.fire({ icon: 'error', title: 'Error', text: err.message, background: '#ffffff', color: '#0f172a' }); }
    else {
      setTmMap((prev) => ({ ...prev, [mod.id]: { tenant_id: tenant.id, module_id: mod.id, is_enabled: newVal, source: src, price_monthly: price } }));
      // Bust the in-memory module cache so the tenant side re-fetches on next access
      clearModuleCache();
    }
    setToggling(null);
  };

  const enabledCount  = modules.filter((m) => isEnabled(m.id)).length;
  const planCount     = modules.filter((m) => planSet.has(m.id) && isEnabled(m.id)).length;
  const addonCount    = modules.filter((m) => !planSet.has(m.id) && isEnabled(m.id)).length;
  const monthlyExtra  = modules.filter((m) => !planSet.has(m.id) && isEnabled(m.id)).reduce((s, m) => s + getPrice(m), 0);

  if (loading) return <SuperAdminLayout><SAHeader title="Módulos del Tenant" subtitle="Cargando…" /><LoadingState /></SuperAdminLayout>;
  if (error || !tenant) return <SuperAdminLayout><SAHeader title="Módulos del Tenant" subtitle="" /><ErrorState message={error ?? 'Tenant no encontrado'} onRetry={load} /></SuperAdminLayout>;

  return (
    <SuperAdminLayout>
      <SAHeader
        title={`Módulos — ${tenant.name}`}
        subtitle={`Plan: ${tenant.plan_code.toUpperCase()}  ·  slug: ${tenant.slug}`}
        actions={
          <Link to="/sa/tenants" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>Tenants
          </Link>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Activos total', value: `${enabledCount}/${modules.length}`, color: 'text-slate-900', bg: 'bg-slate-100', icon: 'ri-apps-line' },
            { label: 'Del plan',      value: planCount,  color: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'ri-shield-check-line' },
            { label: 'Add-ons extra', value: addonCount, color: 'text-amber-600',   bg: 'bg-amber-50',   icon: 'ri-puzzle-line' },
            { label: 'Extra mensual', value: `$${monthlyExtra}`, color: 'text-sky-600', bg: 'bg-sky-50', icon: 'ri-money-dollar-circle-line' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.bg} ${s.color} shrink-0`}><i className={`${s.icon} text-base`}></i></span>
              <div><p className={`text-lg font-bold leading-none ${s.color}`}>{s.value}</p><p className="text-slate-500 text-xs mt-1">{s.label}</p></div>
            </div>
          ))}
        </div>

        {/* Plan info */}
        <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border ${tenant.plan_code === 'enterprise' ? 'bg-emerald-50 border-emerald-200' : tenant.plan_code === 'pro' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
          <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${PLAN_STYLES[tenant.plan_code] ?? 'bg-slate-100 text-slate-500'}`}><i className="ri-vip-crown-line text-sm"></i></span>
          <div className="flex-1">
            <p className="text-slate-700 text-sm">
              Plan <span className={`font-bold uppercase ${tenant.plan_code === 'enterprise' ? 'text-emerald-700' : tenant.plan_code === 'pro' ? 'text-amber-700' : 'text-slate-500'}`}>{tenant.plan_code}</span>
              {' · '}<span className="text-slate-500">{planSet.size} módulos incluidos automáticamente</span>
            </p>
            <p className="text-slate-400 text-xs mt-0.5">Los módulos del plan se activan/desactivan al cambiar el plan del tenant. Los add-ons son facturación adicional.</p>
          </div>
        </div>

        {/* Modules table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_90px_80px_80px_60px] border-b border-slate-200 bg-slate-50">
            <div className="px-5 py-3 text-slate-500 text-xs uppercase tracking-wider">Módulo</div>
            <div className="px-3 py-3 text-slate-500 text-xs uppercase tracking-wider">Origen</div>
            <div className="px-3 py-3 text-slate-500 text-xs uppercase tracking-wider">Tipo</div>
            <div className="px-3 py-3 text-slate-500 text-xs uppercase tracking-wider">Precio</div>
            <div className="px-3 py-3 text-slate-500 text-xs uppercase tracking-wider text-center">On</div>
          </div>

          {CATEGORIES.map((cat) => {
            const catMods = modules.filter((m) => m.category === cat);
            if (catMods.length === 0) return null;
            const cs = CAT_STYLE[cat] ?? CAT_STYLE.Production;
            return (
              <div key={cat}>
                <div className="grid grid-cols-[1fr_90px_80px_80px_60px] border-b border-slate-100 bg-slate-50/50">
                  <div className="px-5 py-2 flex items-center gap-2 col-span-5">
                    <span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`}></span>
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${cs.label}`}>{cat}</span>
                  </div>
                </div>
                {catMods.map((mod, idx) => {
                  const enabled  = isEnabled(mod.id);
                  const source   = getSource(mod.id);
                  const price    = getPrice(mod);
                  const sb       = SOURCE_BADGE[source];
                  const icon     = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
                  const isLast   = idx === catMods.length - 1;
                  const spinning = toggling === mod.id;
                  return (
                    <div key={mod.id} className={`grid grid-cols-[1fr_90px_80px_80px_60px] ${!isLast ? 'border-b border-slate-100' : ''} hover:bg-slate-50/50 transition-colors items-center`}>
                      <div className="px-5 py-3 flex items-center gap-3">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${cs.icon_bg} ${cs.icon_text}`}><i className={`${icon} text-sm`}></i></span>
                        <div>
                          <p className={`text-sm ${enabled ? 'text-slate-900' : 'text-slate-400'}`}>{mod.name}</p>
                          {mod.is_core && <span className="text-[10px] text-emerald-600">Core</span>}
                        </div>
                      </div>
                      <div className="px-3 py-3">
                        {sb ? <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${sb.bg} ${sb.text}`}>{sb.label}</span> : <span className="text-slate-300 text-xs">—</span>}
                      </div>
                      <div className="px-3 py-3">
                        {mod.is_core ? <span className="text-[10px] text-emerald-600">Core</span> : <span className="text-[10px] text-amber-600">Add-on</span>}
                      </div>
                      <div className="px-3 py-3">
                        {price > 0 ? <span className={`text-sm font-medium ${enabled ? 'text-amber-600' : 'text-slate-300'}`}>${price}</span> : <span className="text-slate-300 text-xs">—</span>}
                      </div>
                      <div className="px-3 py-3 flex justify-center">
                        <button onClick={() => toggle(mod)} disabled={!!spinning}
                          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer disabled:opacity-60 ${enabled ? 'bg-blue-600' : 'bg-slate-200'}`}>
                          {spinning ? (
                            <span className="absolute inset-0 flex items-center justify-center"><span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin"></span></span>
                          ) : (
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${enabled ? 'left-5' : 'left-0.5'}`} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-slate-400 text-xs">{enabledCount} de {modules.length} módulos activos</p>
            {monthlyExtra > 0 && <p className="text-amber-600 text-xs">Add-ons: <strong>${monthlyExtra}/mes</strong> extra facturación</p>}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
